import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

import useWorkspaceTabs from '@/data/hook/useWorkspaceTabs';
import { BreadcrumbItem } from '@/data/hook/useDynamicBreadcrumbItems';
import { Breakpoints } from '@/utils/Breakpoint';
import { ROUTES } from '@/utils/routes';
import { Tabs } from '@/components/ui/daisy';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import HeadTitle from '@/components/ui/custom/HeadTitle';
import Layout from '@/components/template/Layout';
import ModernLayout from '@/components/shared/ModernLayout';

import NoAdminScreenForMobile from '@/pages/admin/NoAdminScreenForMobile';
import ActivityPendenciesTab from './tabs/ActivityPendenciesTab';
import AttendancePendenciesTab from './tabs/AttendancePendenciesTab';
import ExamPendenciesTab from './tabs/ExamPendenciesTab';
import { FolderIcon } from '@heroicons/react/24/outline';

export enum PendenciesTabEnum {
  ATTENDANCE = 'attendance',
  EXAMS = 'exams',
  ACTIVITIES = 'activities',
}

export default function PendenciesPage() {
  const { t } = useTranslation('translation', { keyPrefix: 'pendencies' });
  const { t: tBreadcrumb } = useTranslation('translation', {
    keyPrefix: 'breadcrumb',
  });
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ minWidth: Breakpoints.lg });

  const { tab, setTab } = useWorkspaceTabs<PendenciesTabEnum>({
    defaultTab: PendenciesTabEnum.ATTENDANCE,
  });

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      id: 'home',
      href: '/',
      children: tBreadcrumb('home'),
      icon: <FolderIcon className="size-4" />,
    },
    {
      id: 'klasses',
      href: ROUTES.ADMIN.CLASSES.INFO(),
      children: tBreadcrumb('klasses'),
      icon: <FolderIcon className="size-4" />,
    },
  ];

  return (
    <Layout>
      <HeadTitle routeInfo={t('title')} />
      <ConditionalRenderer condition={!isDesktop}>
        <NoAdminScreenForMobile />
      </ConditionalRenderer>
      <ConditionalRenderer condition={isDesktop}>
        <ModernLayout
          items={breadcrumbItems}
          onBack={() => navigate(-1)}
          backLabel={t('back')}
          classNames={{ div: 'overflow-visible' }}
        >
          <section className="flex flex-1 flex-col gap-6 rounded-2xl bg-base-100 p-6">
            <header className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-primary">
                {t('allKlasses')}
              </span>
              <h1 className="text-2xl font-medium text-base-content">
                {t('title')}
              </h1>
            </header>

            <Tabs
              variant="box"
              className="w-fit"
              value={tab}
              onChange={value => setTab(value as PendenciesTabEnum)}
            >
              <Tabs.Tab value={PendenciesTabEnum.ATTENDANCE}>
                {t('tabs.attendance')}
              </Tabs.Tab>
              <Tabs.Tab value={PendenciesTabEnum.EXAMS}>
                {t('tabs.exams')}
              </Tabs.Tab>
              <Tabs.Tab value={PendenciesTabEnum.ACTIVITIES}>
                {t('tabs.activities')}
              </Tabs.Tab>
            </Tabs>

            <ConditionalRenderer
              condition={tab === PendenciesTabEnum.ATTENDANCE}
            >
              <AttendancePendenciesTab />
            </ConditionalRenderer>

            <ConditionalRenderer condition={tab === PendenciesTabEnum.EXAMS}>
              <ExamPendenciesTab />
            </ConditionalRenderer>

            <ConditionalRenderer
              condition={tab === PendenciesTabEnum.ACTIVITIES}
            >
              <ActivityPendenciesTab />
            </ConditionalRenderer>
          </section>
        </ModernLayout>
      </ConditionalRenderer>
    </Layout>
  );
}
